import React, { useState, useRef, useEffect } from 'react';
import {
    Box,
    TextField,
    IconButton,
    Paper,
    Typography,
    Avatar,
    CircularProgress,
} from '@mui/material';
import SendIcon from '@mui/icons-material/Send';
import PersonIcon from '@mui/icons-material/Person';
import SmartToyIcon from '@mui/icons-material/SmartToy';
import {sendChatMessage} from '../../api/chat.js';

const ChatbotComponent = () => {
    const [messages, setMessages] = useState([]);
    const [inputValue, setInputValue] = useState('');
    const [isLoading, setIsLoading] = useState(false);
    const messagesEndRef = useRef(null);
    // const sendChatMessage=useSendChatMessage();

    // Scroll to bottom when new messages arrive
    const scrollToBottom = () => {
        messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    };

    useEffect(() => {
        scrollToBottom();
    }, [messages]);

    // Stub API call - replace with your actual API implementation
    const sendMessageToAPI = async (message) => {
        // TODO: Replace this stub with your actual API call
        // Example:
        // const response = await fetch('YOUR_API_ENDPOINT', {
        //   method: 'POST',
        //   headers: { 'Content-Type': 'application/json' },
        //   body: JSON.stringify({ message })
        // });
        // const data = await response.json();
        // return data.response;

        // Simulated API response for demonstration
        // console.log("Simulating API call for message:",message)
        // return new Promise((resolve) => {
        //     setTimeout(() => {
        //         resolve(`This is a simulated response to: "${message}"`);
        //     }, 1000);
        // });

        console.log("Sending message to API:",message)

        const response = await sendChatMessage(message)
        console.log("Chatbot response:",response)
        return response.reply;
    };

    const handleSendMessage = async () => {
        if (inputValue.trim() === '') return;

        const userMessage = {
            id: Date.now(),
            text: inputValue,
            sender: 'user',
            timestamp: new Date(),
        };

        setMessages((prev) => [...prev, userMessage]);
        setInputValue('');
        setIsLoading(true);

        try {
            const botResponse = await sendMessageToAPI(inputValue);

            const botMessage = {
                id: Date.now() + 1,
                text: botResponse,
                sender: 'bot',
                timestamp: new Date(),
            };

            setMessages((prev) => [...prev, botMessage]);
        } catch (error) {
            console.error('Error sending message:', error);
            const errorMessage = {
                id: Date.now() + 1,
                text: 'Sorry, there was an error processing your message.',
                sender: 'bot',
                timestamp: new Date(),
                isError: true,
            };
            setMessages((prev) => [...prev, errorMessage]);
        } finally {
            setIsLoading(false);
        }
    };

    const handleKeyPress = (e) => {
        if (e.key === 'Enter' && !e.shiftKey) {
            e.preventDefault();
            handleSendMessage();
        }
    };

    return (
        <Paper
            elevation={3}
            sx={{
                height: '600px',
                display: 'flex',
                flexDirection: 'column',
                maxWidth: '800px',
                margin: '0 auto',
            }}
        >
            {/* Header */}
            <Box
                sx={{
                    p: 2,
                    borderBottom: '1px solid',
                    borderColor: 'divider',
                    backgroundColor: 'primary.main',
                    color: 'primary.contrastText',
                }}
            >
                <Typography variant="h6" component="div">
                    Chatbot Assistant
                </Typography>
            </Box>

            {/* Messages Container */}
            <Box
                sx={{
                    flex: 1,
                    overflowY: 'auto',
                    p: 2,
                    backgroundColor: '#f5f5f5',
                }}
            >
                {messages.length === 0 && (
                    <Box
                        sx={{
                            display: 'flex',
                            justifyContent: 'center',
                            alignItems: 'center',
                            height: '100%',
                        }}
                    >
                        <Typography variant="body1" color="text.secondary">
                            Start a conversation...
                        </Typography>
                    </Box>
                )}

                {messages.map((message) => (
                    <Box
                        key={message.id}
                        sx={{
                            display: 'flex',
                            justifyContent: message.sender === 'user' ? 'flex-end' : 'flex-start',
                            mb: 2,
                        }}
                    >
                        <Box
                            sx={{
                                display: 'flex',
                                flexDirection: message.sender === 'user' ? 'row-reverse' : 'row',
                                alignItems: 'flex-start',
                                maxWidth: '70%',
                            }}
                        >
                            <Avatar
                                sx={{
                                    bgcolor: message.sender === 'user' ? 'primary.main' : 'secondary.main',
                                    mx: 1,
                                }}
                            >
                                {message.sender === 'user' ? <PersonIcon /> : <SmartToyIcon />}
                            </Avatar>
                            <Paper
                                elevation={1}
                                sx={{
                                    p: 2,
                                    backgroundColor: message.sender === 'user' ? 'primary.light' : 'white',
                                    color: message.sender === 'user' ? 'primary.contrastText' : 'text.primary',
                                    borderRadius: 2,
                                    ...(message.isError && { backgroundColor: 'error.light' }),
                                }}
                            >
                                <Typography variant="body1" sx={{ whiteSpace: 'pre-wrap' }}>
                                    {message.text}
                                </Typography>
                            </Paper>
                        </Box>
                    </Box>
                ))}

                {isLoading && (
                    <Box sx={{ display: 'flex', justifyContent: 'flex-start', mb: 2 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center' }}>
                            <Avatar sx={{ bgcolor: 'secondary.main', mx: 1 }}>
                                <SmartToyIcon />
                            </Avatar>
                            <Paper elevation={1} sx={{ p: 2 }}>
                                <CircularProgress size={24} />
                            </Paper>
                        </Box>
                    </Box>
                )}

                <div ref={messagesEndRef} />
            </Box>

            {/* Input Area */}
            <Box
                sx={{
                    p: 2,
                    borderTop: '1px solid',
                    borderColor: 'divider',
                    backgroundColor: 'background.paper',
                }}
            >
                <Box sx={{ display: 'flex', gap: 1 }}>
                    <TextField
                        fullWidth
                        multiline
                        maxRows={4}
                        variant="outlined"
                        placeholder="Type your message..."
                        value={inputValue}
                        onChange={(e) => setInputValue(e.target.value)}
                        onKeyPress={handleKeyPress}
                        disabled={isLoading}
                    />
                    <IconButton
                        color="primary"
                        onClick={handleSendMessage}
                        disabled={isLoading || inputValue.trim() === ''}
                        sx={{
                            backgroundColor: 'primary.main',
                            color: 'white',
                            '&:hover': {
                                backgroundColor: 'primary.dark',
                            },
                            '&:disabled': {
                                backgroundColor: 'action.disabledBackground',
                            },
                        }}
                    >
                        <SendIcon />
                    </IconButton>
                </Box>
            </Box>
        </Paper>
    );
};

export default ChatbotComponent;